import React from 'react'
import { Container } from '../../../components'
import {
    Avatar,
    Button,
    MenuItem,
    Popover,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import { Rating } from '@material-ui/lab'
import { NVLVendor } from '.'
import { axiosInstance, getInitials, isAccountManager } from '../../../helpers'
import { ReactComponent as MapIcon } from '../../../assets/map-pin.svg'
import { ReactComponent as RibbonThumbGreenIcon } from '../../../assets/icons/thumbs_ribbon-32-filled-big.svg'
import { ReactComponent as RibbonAirplaneDarkIcon } from '../../../assets/icons/airplane_ribbon-32-filled-big.svg'
import SkillCatLogoSmall from '../../../assets/SkillCatLogo_small.png'
import {
    AssignmentTurnedInOutlined,
    AttachMoney,
    EmailOutlined,
    GroupAdd,
    SupervisorAccount,
} from '@material-ui/icons'
import { useAppDispatch, useUser } from '../../../hooks'
import { UpdatedPricingRequest, addVendorToApt } from '../../../store'
import { toast } from 'react-toastify'
import { PlanStatus } from '../../../models'
import {
    NVLVendorTier,
    NVL_TIER_LABELS,
    NVL_TIER_VOLUME_ROWS,
} from './nvlVendorModel'
import { SendVendorEmailDialog } from './SendVendorEmailDialog'

const TierVolumeTooltip = () => (
    <div style={{ padding: 4 }}>
        <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 12 }}>
            Tiers by total work-order volume
        </div>
        <div style={{ fontSize: 11, marginBottom: 6, opacity: 0.9 }}>
            Based on all work orders (not only completed).
        </div>
        <table
            style={{
                borderCollapse: 'collapse',
                fontSize: 12,
                lineHeight: 1.4,
            }}
        >
            <thead>
                <tr>
                    <th
                        style={{
                            textAlign: 'left',
                            padding: '2px 10px 4px 0',
                            fontWeight: 600,
                        }}
                    >
                        Work orders
                    </th>
                    <th
                        style={{
                            textAlign: 'left',
                            padding: '2px 0 4px 0',
                            fontWeight: 600,
                        }}
                    >
                        Tier
                    </th>
                </tr>
            </thead>
            <tbody>
                {NVL_TIER_VOLUME_ROWS.map((row) => (
                    <tr key={row.tier}>
                        <td style={{ padding: '2px 10px 2px 0' }}>
                            {row.volume}
                        </td>
                        <td style={{ padding: '2px 0' }}>{row.tier}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
)

interface Props {
    vendor: NVLVendor
    isSelected: boolean
    setSelectedVendor: (vendor: NVLVendor) => void
    requestUpdatedPricing: (req: UpdatedPricingRequest) => Promise<void>
}

export const VendorListRow = (props: Props) => {
    const {
        vendor,
        isSelected,
        setSelectedVendor,
        requestUpdatedPricing,
    } = props

    const theme = useTheme()
    const dispatch = useAppDispatch()
    const { workspaceUser } = useUser()
    const [amAnchorEl, setAmAnchorEl] = React.useState<HTMLElement | null>(null)
    const [isTrained, setIsTrained] = React.useState(vendor.is_trained)
    const [emailDialogOpen, setEmailDialogOpen] = React.useState(false)

    React.useEffect(() => {
        setIsTrained(vendor.is_trained)
    }, [vendor.id, vendor.is_trained])

    const displayAddress =
        vendor.state !== '' || vendor.address !== '' || vendor.city !== ''

    const tier = (vendor.tier ?? 'silver') as NVLVendorTier
    const tierColor =
        tier === 'silver'
            ? theme.palette.silver.main
            : tier === 'gold'
            ? theme.palette.gold.main
            : tier === 'blue'
            ? theme.palette.blue.main
            : tier === 'dark_blue'
            ? theme.palette.darkBlue.main
            : theme.palette.primary.dark

    const formatCompleteCount = (count: number) => {
        if (count >= 1000) {
            return `${Math.floor(count / 1000)}K+`
        }
        if (count >= 100) {
            return `${Math.floor(count / 100) * 100}+`
        }
        return String(count)
    }

    const ratingValue = vendor.rating ?? 0
    const hasRating = ratingValue > 0
    const isAm = isAccountManager(workspaceUser)
    const amMenuOpen = Boolean(amAnchorEl)

    const closeAmMenu = () => setAmAnchorEl(null)

    const setTrainedStatus = (nextTrained: boolean) => {
        axiosInstance
            .patch(`company/vendor/${vendor.id}/`, {
                is_trained: nextTrained,
            })
            .then(() => {
                vendor.is_trained = nextTrained
                setIsTrained(nextTrained)
                toast.success(
                    nextTrained
                        ? `${vendor.name} set as trained`
                        : `${vendor.name} marked as untrained`,
                )
                closeAmMenu()
            })
            .catch(() => {
                toast.error('Unable to update trained status')
            })
    }

    const removeFromNvl = () => {
        axiosInstance
            .post(
                `company/vendor/${vendor.id}/remove_from_national_vendor_list/`,
            )
            .then(() => {
                toast.success(`${vendor.name} will no longer be on the nvl`)
                closeAmMenu()
            })
            .catch((e) => {
                toast.error(e)
            })
    }

    const actionButtonStyle: React.CSSProperties = {
        justifyContent: 'flex-start',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: 12,
        lineHeight: 1.2,
        padding: theme.spacing(0.25, 1),
        minHeight: 28,
        color: theme.palette.text.primary,
        whiteSpace: 'nowrap',
    }

    // Fixed column widths keep metrics aligned across rows.
    const col = {
        tier: 96,
        rating: 100,
        distance: 72,
        complete: 76,
        goBacks: 76,
        woCount: 88,
        badges: 100,
    }

    return (
        <Container
            style={{
                width: '100%',
                maxWidth: 1320,
                border: `1px solid ${theme.palette.grey[400]}`,
                borderRadius: '5px',
                margin: theme.spacing(1, 0),
                cursor: 'pointer',
                backgroundColor: isSelected
                    ? theme.palette.lightGrey.light
                    : '',
                minHeight: 120,
                alignItems: 'center',
                position: 'relative',
                overflow: 'visible',
            }}
            onClick={() => {
                setSelectedVendor(vendor)
            }}
        >
            <Container
                direction="column"
                alignItems="center"
                style={{
                    margin: theme.spacing(2),
                    position: 'relative',
                    flexShrink: 0,
                }}
            >
                <div
                    style={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        border: `3px solid ${tierColor}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        backgroundColor: '#fff',
                    }}
                >
                    {vendor.profile_picture !== '' ? (
                        <img
                            src={vendor.profile_picture}
                            alt=""
                            style={{
                                width: '56px',
                                height: '56px',
                                objectFit: 'cover',
                            }}
                        />
                    ) : (
                        <Avatar
                            variant="square"
                            style={{
                                backgroundColor: theme.palette.primary.dark,
                                width: '56px',
                                height: '56px',
                                fontSize: 18,
                            }}
                        >
                            {getInitials(vendor.name)}
                        </Avatar>
                    )}
                </div>
                <strong
                    style={{
                        display: 'block',
                        fontSize: 12,
                        fontWeight: 700,
                        color: theme.palette.grey[800],
                        marginTop: theme.spacing(0.5),
                        whiteSpace: 'nowrap',
                        letterSpacing: 0.2,
                    }}
                >
                    EZID {vendor.id}
                </strong>
            </Container>

            <Container
                style={{
                    margin: theme.spacing(2),
                    justifyContent: 'center',
                    width: 280,
                    minWidth: 220,
                }}
                direction="column"
            >
                <Container
                    style={{
                        fontWeight: 600,
                        fontSize: 18,
                        marginBottom: theme.spacing(0.5),
                    }}
                >
                    {vendor.name}
                </Container>

                {displayAddress && (
                    <Container
                        style={{
                            color: theme.palette.darkGreen.main,
                            fontSize: 13,
                            fontWeight: 400,
                            alignItems: 'center',
                        }}
                    >
                        <MapIcon style={{ marginRight: theme.spacing(1) }} />
                        {`${vendor.address} ${vendor.city}, ${vendor.state}`}
                    </Container>
                )}
            </Container>

            <Container
                alignItems="center"
                style={{
                    margin: theme.spacing(0, 2),
                    gap: theme.spacing(2),
                    flexWrap: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                }}
            >
                <Container
                    direction="column"
                    style={{
                        width: col.tier,
                        minWidth: col.tier,
                        maxWidth: col.tier,
                        gap: theme.spacing(0.5),
                        flexShrink: 0,
                        alignItems: 'stretch',
                    }}
                >
                    <Tooltip
                        title={<TierVolumeTooltip />}
                        interactive
                        placement="bottom-start"
                    >
                        <span style={{ display: 'block', width: '100%' }}>
                            <Container
                                alignItems="center"
                                justifyContent="center"
                                style={{
                                    width: '100%',
                                    backgroundColor: `${tierColor}22`,
                                    border: `1px solid ${tierColor}`,
                                    borderRadius: 16,
                                    padding: theme.spacing(0.25, 0.5),
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: theme.palette.text.primary,
                                    whiteSpace: 'nowrap',
                                    boxSizing: 'border-box',
                                }}
                            >
                                {NVL_TIER_LABELS[tier]}
                            </Container>
                        </span>
                    </Tooltip>
                    {vendor.plan === PlanStatus.PREMIUM && (
                        <Container
                            alignItems="center"
                            justifyContent="center"
                            style={{
                                width: '100%',
                                backgroundColor: theme.palette.darkGreen.main,
                                border: `1px solid ${theme.palette.darkGreen.main}`,
                                borderRadius: 16,
                                padding: theme.spacing(0.25, 0.5),
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#fff',
                                whiteSpace: 'nowrap',
                                boxSizing: 'border-box',
                            }}
                        >
                            Featured
                        </Container>
                    )}
                </Container>

                <Container
                    direction="column"
                    alignItems="flex-start"
                    style={{
                        width: col.rating,
                        minWidth: col.rating,
                        maxWidth: col.rating,
                        flexShrink: 0,
                    }}
                >
                    <Rating
                        name={`nvl-row-rating-${vendor.id}`}
                        value={hasRating ? ratingValue : 0}
                        precision={0.5}
                        readOnly
                        size="small"
                    />
                    <span
                        style={{
                            fontSize: 11,
                            color: theme.palette.grey[600],
                            marginTop: 2,
                        }}
                    >
                        {hasRating ? ratingValue.toFixed(1) : 'No rating'}
                    </span>
                </Container>

                <Container
                    direction="column"
                    style={{
                        width: col.distance,
                        minWidth: col.distance,
                        maxWidth: col.distance,
                        flexShrink: 0,
                    }}
                >
                    <span
                        style={{
                            fontSize: 11,
                            color: theme.palette.grey[600],
                        }}
                    >
                        Distance
                    </span>
                    <span
                        style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#102A12',
                        }}
                    >
                        {vendor.distance != null
                            ? `${vendor.distance} mi`
                            : '—'}
                    </span>
                </Container>

                <Container
                    direction="column"
                    style={{
                        width: col.complete,
                        minWidth: col.complete,
                        maxWidth: col.complete,
                        flexShrink: 0,
                    }}
                >
                    <span
                        style={{
                            fontSize: 11,
                            color: theme.palette.grey[600],
                        }}
                    >
                        Complete
                    </span>
                    <span
                        style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#102A12',
                        }}
                    >
                        {(vendor.completion_pct ?? 0).toFixed(1)}%
                    </span>
                </Container>

                <Container
                    direction="column"
                    style={{
                        width: col.goBacks,
                        minWidth: col.goBacks,
                        maxWidth: col.goBacks,
                        flexShrink: 0,
                    }}
                >
                    <span
                        style={{
                            fontSize: 11,
                            color: theme.palette.grey[600],
                        }}
                    >
                        Go backs
                    </span>
                    <span
                        style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#102A12',
                        }}
                    >
                        {(vendor.go_back_rate ?? 0).toFixed(1)}%
                    </span>
                </Container>

                <Tooltip
                    title={`${vendor.complete_count} completed work orders`}
                >
                    <span
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: col.woCount,
                            minWidth: col.woCount,
                            maxWidth: col.woCount,
                            flexShrink: 0,
                            cursor: 'default',
                        }}
                    >
                        <span
                            style={{
                                fontSize: 11,
                                color: theme.palette.grey[600],
                            }}
                        >
                            Completed
                        </span>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: theme.spacing(0.5),
                            }}
                        >
                            <AssignmentTurnedInOutlined
                                style={{
                                    fontSize: 18,
                                    color: theme.palette.darkGreen.main,
                                }}
                            />
                            <span
                                style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: '#102A12',
                                }}
                            >
                                {formatCompleteCount(
                                    vendor.complete_count || 0,
                                )}
                            </span>
                        </span>
                    </span>
                </Tooltip>

                <Container
                    alignItems="center"
                    style={{
                        width: col.badges,
                        minWidth: col.badges,
                        maxWidth: col.badges,
                        gap: theme.spacing(0.5),
                        flexShrink: 0,
                    }}
                >
                    {isTrained && (
                        <Tooltip title="EZ Trained">
                            <span style={{ display: 'flex' }}>
                                <RibbonThumbGreenIcon />
                            </span>
                        </Tooltip>
                    )}
                    {vendor.will_travel && (
                        <Tooltip title="Will Travel">
                            <span style={{ display: 'flex' }}>
                                <RibbonAirplaneDarkIcon />
                            </span>
                        </Tooltip>
                    )}
                    {vendor.is_skillcat && (
                        <Tooltip title="SkillCat">
                            <img src={SkillCatLogoSmall} alt="Skill Cat Logo" />
                        </Tooltip>
                    )}
                </Container>
            </Container>

            <Container
                direction="column"
                style={{
                    margin: theme.spacing(1, 1.5, 1, 0),
                    gap: 2,
                    flexShrink: 0,
                    minWidth: 170,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {workspaceUser && (
                    <Button
                        style={actionButtonStyle}
                        startIcon={<GroupAdd style={{ fontSize: 16 }} />}
                        onClick={() => {
                            dispatch(
                                addVendorToApt({
                                    body: { vendor: vendor.id },
                                }),
                            )
                            toast.success('Added!', {
                                position: 'bottom-center',
                                autoClose: 1000,
                            })
                        }}
                    >
                        Add to Workforce
                    </Button>
                )}
                <Button
                    style={actionButtonStyle}
                    startIcon={<AttachMoney style={{ fontSize: 16 }} />}
                    onClick={() => {
                        requestUpdatedPricing({
                            body: { vendor_id: vendor.id },
                        })
                    }}
                >
                    Request Pricing
                </Button>
                <Button
                    style={actionButtonStyle}
                    startIcon={<EmailOutlined style={{ fontSize: 16 }} />}
                    onClick={() => {
                        if (!workspaceUser) {
                            toast.error('Please log in to send email')
                            return
                        }
                        if (!vendor.email) {
                            toast.error('This vendor has no email on file')
                            return
                        }
                        setEmailDialogOpen(true)
                    }}
                >
                    Send Email
                </Button>
                {isAm && (
                    <>
                        <Button
                            style={actionButtonStyle}
                            startIcon={
                                <SupervisorAccount style={{ fontSize: 16 }} />
                            }
                            onClick={(e) => {
                                setAmAnchorEl(e.currentTarget)
                            }}
                        >
                            Account Managers
                        </Button>
                        <Popover
                            open={amMenuOpen}
                            anchorEl={amAnchorEl}
                            onClose={closeAmMenu}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                        >
                            <Container
                                direction="column"
                                style={{
                                    minWidth: 200,
                                    padding: theme.spacing(0.5, 0),
                                }}
                            >
                                <MenuItem
                                    dense
                                    disabled={isTrained}
                                    onClick={() => setTrainedStatus(true)}
                                >
                                    Mark as Trained
                                </MenuItem>
                                <MenuItem
                                    dense
                                    disabled={!isTrained}
                                    onClick={() => setTrainedStatus(false)}
                                >
                                    Mark as Untrained
                                </MenuItem>
                                <MenuItem dense onClick={removeFromNvl}>
                                    Remove from NVL
                                </MenuItem>
                            </Container>
                        </Popover>
                    </>
                )}
            </Container>
            <SendVendorEmailDialog
                open={emailDialogOpen}
                onClose={() => setEmailDialogOpen(false)}
                vendorId={vendor.id}
                vendorEmail={vendor.email}
                defaultReplyTo={workspaceUser?.email || ''}
                defaultFromName={workspaceUser?.name || ''}
            />
        </Container>
    )
}
